/**
 * Cost Center Mappings List Page Component
 * /admin/cost-center-mappings
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import apiClient from '../api/client';
import { exportToCSV } from '../utils/export';

const CostCenterMappings = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [customers, setCustomers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadMappings();
    loadCustomers();
  }, [customerFilter, departmentFilter, activeFilter]);

  const loadCustomers = async () => {
    try {
      const response = await apiClient.getCustomers({ is_active: true });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setCustomers(data);
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  };

  const loadMappings = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (customerFilter) params.customer = customerFilter;
      if (departmentFilter) params.department = departmentFilter;
      if (activeFilter !== '') params.is_active = activeFilter === 'true';
      if (searchTerm) params.search = searchTerm;
      
      const response = await apiClient.getCostCenterMappings(params);
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setMappings(data);
    } catch (err) {
      setError(err.message || 'Failed to load cost center mappings');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadMappings();
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Are you sure you want to delete cost center mapping "${code}"?`)) {
      return;
    }
    try {
      await apiClient.deleteCostCenterMapping(id);
      setError('');
      loadMappings();
    } catch (err) {
      setError(err.message || 'Failed to delete cost center mapping');
    }
  };

  if (loading && mappings.length === 0) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Cost Center Mappings
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => {
              const headers = [
                { key: 'customer_name', label: 'Customer' },
                { key: 'cost_center_code', label: 'Cost Center Code' },
                { key: 'department', label: 'Department' },
                { key: 'description', label: 'Description' },
                { key: 'is_active', label: 'Active' },
              ];
              exportToCSV(mappings, 'cost-center-mappings', headers);
            }}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/cost-center-mappings/new')}
          >
            New Mapping
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <form onSubmit={handleSearchSubmit} style={{ flex: 1, minWidth: 200 }}>
            <TextField
              fullWidth
              placeholder="Search by cost center code..."
              value={searchTerm}
              onChange={handleSearch}
              slotProps={{ input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              } }}
            />
          </form>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              value={customerFilter}
              label="Customer"
              onChange={(e) => setCustomerFilter(e.target.value)}
            >
              <MenuItem value="">All Customers</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.customer_name || customer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Department</InputLabel>
            <Select
              value={departmentFilter}
              label="Department"
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <MenuItem value="">All Departments</MenuItem>
              <MenuItem value="operations">Operations</MenuItem>
              <MenuItem value="sales">Sales</MenuItem>
              <MenuItem value="management">Management</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={activeFilter}
              label="Status"
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Active</MenuItem>
              <MenuItem value="false">Inactive</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Cost Center Code</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No cost center mappings found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              mappings.map((mapping) => (
                <TableRow key={mapping.id} hover>
                  <TableCell>
                    {mapping.customer_name || mapping.customer_code || '-'}
                  </TableCell>
                  <TableCell>{mapping.cost_center_code}</TableCell>
                  <TableCell>{mapping.department || '-'}</TableCell>
                  <TableCell>{mapping.description || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={mapping.is_active ? 'Active' : 'Inactive'}
                      color={mapping.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/cost-center-mappings/${mapping.id}`)}
                      title="View"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/admin/cost-center-mappings/${mapping.id}/edit`)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(mapping.id, mapping.cost_center_code)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default CostCenterMappings;

